import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { TeamSpot } from '@optools/components/team-spot';
import { AlertDialogDescription } from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import { usePolling } from '@optools/hooks/usePolling';
import { PROVISION_MEDIA } from '@optools/services/operator-gateway/btc/mutations';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import {
  BtcScheduleContent,
  BtcScheduleGame,
  BtcSchedulePipeline,
} from '@optools/services/operator-gateway/btc/types';
import {
  MediaFeedType,
  PipelineStatus,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { toast } from 'sonner';

const getTeamByFeedType = (game: BtcScheduleGame, feedType: MediaFeedType) => {
  if (feedType === MediaFeedType.HOME) {
    return game.home;
  }

  if (feedType === MediaFeedType.AWAY) {
    return game.away;
  }

  return null;
};

export interface ProvisionPipelineButtonProps {
  content: BtcScheduleContent;
  game: BtcScheduleGame;
  pipelines: BtcSchedulePipeline[];
  scheduleDate: string;
}

export function ProvisionPipelineButton({
  content,
  game,
  pipelines,
  scheduleDate,
}: ProvisionPipelineButtonProps) {
  const { callSign, feedType, mediaId } = content;

  const [provisionMedia, { loading }] = useMutation(PROVISION_MEDIA, {
    onCompleted: ({ provisionMedia }) => {
      const [{ mediaId, reason, success }] = provisionMedia;
      if (success) {
        toast.success('Provision Media Success', { description: `Media ID: ${mediaId}` });
        startPolling();
      } else {
        toast.error(`Provision Media Error`, {
          description: (
            <>
              <h2>Media ID: {mediaId}</h2>
              <p>{reason}</p>
            </>
          ),
          duration: Infinity,
        });
      }
    },
  });

  const { isPolling, startPolling } = usePolling({
    interval: 2000,
    onMaxAttemptsReached: () => {
      toast.warning('Failed to Fetch Provisioned Pipeline', {
        description:
          'Please refresh the page to ensure that the pipeline has been provisioned correctly.',
        duration: Infinity,
      });
    },
    query: BTC_GAME_PIPELINE,
    shouldStopPolling: ({ game }) => {
      if (!game?.pipelines) return;

      const hasUpdatedPipeline = game?.pipelines.some((pipeline) => {
        return (
          pipeline.media.some((media) => media.id === mediaId) &&
          pipeline.status !== PipelineStatus.ARCHIVED
        );
      });

      return hasUpdatedPipeline;
    },
    variables: { gamePk: game.gamePk, scheduleDate },
  });

  const hasActivePipeline = pipelines.some((pipeline) => {
    return (
      pipeline.media.some((mediaItem) => mediaItem.id === mediaId) &&
      ![(PipelineStatus.ENDED, PipelineStatus.ARCHIVED)].includes(pipeline?.status)
    );
  });

  const team = getTeamByFeedType(game, feedType);
  const isLoading = isPolling || loading;

  return (
    <ConfirmationDialog
      description={
        <AlertDialogDescription data-testid="pipeline-provision-confirmation-description">
          Are you sure you want to provision the{' '}
          <strong>
            {feedType} ({callSign})
          </strong>{' '}
          pipeline?
        </AlertDialogDescription>
      }
      title="Provision Pipeline"
      onConfirm={() => provisionMedia({ variables: { mediaIds: [mediaId] } })}>
      <Button disabled={hasActivePipeline || isLoading} loading={isLoading}>
        {team ? <TeamSpot showAbbreviation={false} team={team} /> : null}
        Provision {feedType} ({callSign})
      </Button>
    </ConfirmationDialog>
  );
}
